import { useRef } from "react";
import { useThree, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import {
  isPinnedWorldMoverSettled,
  shouldSnapOnDrill,
  stepPinnedWorldMover,
  type CosmosWorldNavLevel,
  type OverviewFrame,
  type PinnedCameraFrame,
  type WorldMoverMode,
} from "../lib/cosmos-world-mover";

const ORBIT_MAX_DISTANCE = 120;

export interface PinnedCosmosWorldMoverProps {
  groupRef: React.RefObject<THREE.Group>;
  focusTarget: THREE.Vector3;
  focusKey: string;
  navLevel: CosmosWorldNavLevel;
  clusterRadius?: number;
  overviewFrame?: OverviewFrame | null;
  clusterOverviewFrame?: OverviewFrame | null;
  viewResetKey?: string;
  pinnedCamera: (level: CosmosWorldNavLevel, radius?: number) => PinnedCameraFrame;
  onHudProject: (pos: { x: number; y: number } | null) => void;
  hudWorldPosition?: THREE.Vector3 | null;
  hudOffsetY?: number;
  hudScreenOffsetY?: number;
  initialCameraPos?: [number, number, number];
}

export function PinnedCosmosWorldMover({
  groupRef,
  focusTarget,
  focusKey,
  navLevel,
  clusterRadius,
  overviewFrame = null,
  clusterOverviewFrame = null,
  viewResetKey,
  pinnedCamera,
  onHudProject,
  hudWorldPosition = null,
  hudOffsetY = 2.8,
  hudScreenOffsetY = 8,
  initialCameraPos = [0, 18, 42],
}: PinnedCosmosWorldMoverProps) {
  const { camera, size } = useThree();
  const mode = useRef<WorldMoverMode>("pinned");
  const currentGroupOffset = useRef(new THREE.Vector3(0, 0, 0));
  const targetGroupOffset = useRef(new THREE.Vector3(0, 0, 0));
  const currentCameraPos = useRef(new THREE.Vector3(...initialCameraPos));
  const currentLookAt = useRef(new THREE.Vector3(0, 0, 0));
  const targetCameraPos = useRef(new THREE.Vector3(...initialCameraPos));
  const targetLookAt = useRef(new THREE.Vector3(0, 0, 0));
  const controlsRef = useRef<any>(null);
  const prevViewResetKey = useRef(viewResetKey);
  const prevNavLevel = useRef<CosmosWorldNavLevel>(navLevel);
  const prevFocusKey = useRef(focusKey);

  if (viewResetKey != null && viewResetKey !== prevViewResetKey.current) {
    prevViewResetKey.current = viewResetKey;
    mode.current = "overview-transition";
  }

  const drillSnap = shouldSnapOnDrill(prevNavLevel.current, navLevel);

  if (navLevel !== prevNavLevel.current) {
    prevNavLevel.current = navLevel;
    if (mode.current !== "overview-transition") {
      mode.current = "pinned";
    }
  }

  if (focusKey !== prevFocusKey.current) {
    prevFocusKey.current = focusKey;
    if (mode.current !== "overview-transition") {
      mode.current = "pinned";
    }
  }

  useFrame(() => {
    const group = groupRef.current;
    if (!group) return;

    const isFree = mode.current === "free";
    const activeOverviewFrame =
      mode.current === "overview-transition"
        ? (clusterOverviewFrame ?? overviewFrame)
        : null;

    const result = stepPinnedWorldMover({
      mode: mode.current,
      focusTarget,
      navLevel,
      clusterRadius,
      overviewFrame: activeOverviewFrame,
      drillSnap,
      skipCameraPin: isFree,
      currentGroupOffset: currentGroupOffset.current,
      targetGroupOffset: targetGroupOffset.current,
      currentCameraPos: currentCameraPos.current,
      currentLookAt: currentLookAt.current,
      targetCameraPos: targetCameraPos.current,
      targetLookAt: targetLookAt.current,
      pinnedCamera,
    });

    if (result.mode === "pinned" && !isFree) {
      const groupSettled = isPinnedWorldMoverSettled(
        currentGroupOffset.current,
        targetGroupOffset.current,
      );
      if (groupSettled) {
        mode.current = "free";
        const controls = controlsRef.current;
        if (controls) {
          controls.target.set(0, 0, 0);
          controls.update();
        }
      } else {
        mode.current = "pinned";
      }
    } else if (!isFree) {
      mode.current = result.mode;
    }

    group.position.copy(currentGroupOffset.current);

    if (!isFree && mode.current !== "free") {
      camera.position.copy(currentCameraPos.current);
      camera.lookAt(currentLookAt.current);
    }

    const controls = controlsRef.current;
    if (controls) {
      controls.enabled = mode.current === "free";
    }

    if (hudWorldPosition) {
      const hudWorldPos = hudWorldPosition.clone().add(currentGroupOffset.current);
      hudWorldPos.y += hudOffsetY;
      hudWorldPos.project(camera);
      const sx = (hudWorldPos.x * 0.5 + 0.5) * size.width;
      const sy = (-hudWorldPos.y * 0.5 + 0.5) * size.height;
      onHudProject({ x: sx, y: sy + hudScreenOffsetY });
    } else {
      onHudProject(null);
    }
  });

  return (
    <OrbitControls
      ref={controlsRef}
      enabled={false}
      enableDamping
      dampingFactor={0.08}
      minDistance={2}
      maxDistance={ORBIT_MAX_DISTANCE}
      target={[0, 0, 0]}
      onStart={() => {
        mode.current = "free";
      }}
    />
  );
}
